import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lightning, List, X, Bell } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (user?.role !== "admin") { setPending(0); return; }
    const load = () => api.get("/admin/pending-counts").then((r) => setPending(r.data.total || 0)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  const navItems = [
    { to: "/workers", label: "Find Workers" },
    { to: "/jobs", label: "Browse Jobs" },
    { to: "/virtual", label: "Hire Virtual Staff" },
    { to: "/pricing", label: "For Pros" },
    { to: "/blog", label: "Blog" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src="/canhiresolution-logo.png" alt="CanHireSolution.com" className="h-10 w-auto max-w-[180px] object-contain" />
          <span className="font-display text-xl text-slate-900 hidden sm:inline">
            Can<span className="text-accent-red">Hire</span>Solution<span className="text-accent-red">.com</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`px-3 py-2 text-sm font-medium ${loc.pathname.startsWith(n.to) ? "text-brand" : "text-slate-700 hover:text-slate-900"}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="relative hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900" data-testid="admin-nav-link">
                  <Bell size={16} weight={pending > 0 ? "fill" : "regular"} className={pending > 0 ? "text-accent-red" : ""} />
                  Admin
                  {pending > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-accent-red text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="admin-pending-badge">
                      {pending > 99 ? "99+" : pending}
                    </span>
                  )}
                </Link>
              )}
              <Link to="/dashboard" className="hidden sm:inline-block text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2" data-testid="nav-dashboard">
                Dashboard
              </Link>
              <button
                onClick={logout}
                className="hidden sm:inline-block bg-white border border-slate-200 text-slate-900 px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                data-testid="logout-btn"
              >
                Sign out
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-[#122148]"
              data-testid="signin-btn"
            >
              Sign in
            </button>
          )}
          <button
            className="md:hidden p-2"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-slate-700" data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-sm font-medium text-slate-700">Dashboard</Link>
                <button onClick={logout} className="text-left py-2 text-sm font-medium text-slate-700">Sign out</button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </header>
  );
}
